import { useState, useEffect, useMemo, useCallback } from "react";
import { Upload } from "antd";
import {
  DeleteIcon,
  FileIcon,
  FileItemContainer,
  FileItemContent,
  NotesOptional,
  UploadersScrollArea,
  UploadIcon,
  UploadWrapper,
  ModalBtnsContainer,
  ModalActionBtns,
  ModalTitle,
  SendModal,
  DraggerText,
  FileNameSpan,
  ExtensionInformation,
  ObsContainer,
  ObsInput,
  ClickHere,
} from "./styles";
import { useIntl } from "react-intl";
import api from "../../../service";
import { useSelector, useDispatch } from "react-redux";
import { checkMandatoryDocuments } from "../../../redux/modules/documentRequired/actions";
import useNotification from "../../../hooks/newUXNotification";
import axios from "axios";

const MAX_OBSERVATION_LENGTH = 255;
const NO_EXTENSION_PLACEHOLDER = "-";
const SINGLE_KEY = "single";
const CREATE_UPLOAD_PATH = "/upload-flow/create-upload";
const VALIDATE_UPLOAD_PATH = "/upload-flow/validate-upload";
const DOCUMENT_PATH = (documentId) => `/upload-flow/documents/${documentId}`;

// Remove acentos para padronizar nome do arquivo enviado ao S3
const removeAccents = (str) =>
  str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const getFileExtension = (fileName = "") => fileName.split(".").pop();

const SendDocumentModal = ({
  visible,
  onFinish,
  documentId,
  documentName,
  flowId,
  flowName,
  opportunityId,
  eventId,
  allowedFileExtensions = NO_EXTENSION_PLACEHOLDER,
  isPackage = false,
  packageDocuments = [],
}) => {
  const { Dragger } = Upload;
  const intl = useIntl();
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.auth);
  const notification = useNotification();

  const [fileLists, setFileLists] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [observation, setObservation] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const uploaders = useMemo(() => {
    if (
      isPackage &&
      Array.isArray(packageDocuments) &&
      packageDocuments.length > 0
    ) {
      return packageDocuments.map((doc) => ({
        key: String(doc.documentConfigId ?? doc.id),
        documentConfigId: doc.documentConfigId ?? doc.id,
        name: doc.fileName ?? documentName,
        fileTypes: doc.fileTypes ?? [], // ← extensões específicas de cada documento
      }));
    }
    return [
      { key: SINGLE_KEY, documentConfigId: documentId, name: documentName },
    ];
  }, [isPackage, packageDocuments, documentId, documentName]);

  // Calcula extensões aceitas a partir de um array ou string de tipos
  const buildAcceptExtensions = useCallback((fileTypes) => {
    if (!fileTypes?.length) return undefined;
    const extList = Array.isArray(fileTypes)
      ? fileTypes
      : String(fileTypes).split(",").filter(Boolean);
    return extList
      .map((ext) => {
        const trimmed = ext.trim();
        return trimmed.startsWith(".") ? trimmed : `.${trimmed}`;
      })
      .join(",");
  }, []);

  // String de "accept" para o Dragger
  const acceptExtensions = useMemo(() => {
    if (
      !allowedFileExtensions ||
      allowedFileExtensions === NO_EXTENSION_PLACEHOLDER
    ) {
      return undefined;
    }
    return allowedFileExtensions
      .split(",")
      .map((ext) => {
        const trimmed = ext.trim();
        return trimmed.startsWith(".") ? trimmed : `.${trimmed}`;
      })
      .join(",");
  }, [allowedFileExtensions]);

  // Limpa estado ao fechar a modal
  useEffect(() => {
    if (!visible) {
      setFileLists({});
      setFileErrors({});
      setObservation("");
      setIsUploading(false);
    }
  }, [visible]);

  const buildAuthHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "*/*",
    Authorization: `Bearer ${token}`,
  });

  // Monta um DocumentExecutionRequest para o backend
  const buildExecutionRequest = ({ documentConfigId, file }) => ({
    opportunityId,
    eventId,
    observation,
    uploadedBy: user.id,
    documentConfigId,
    uploadFlowId: flowId,
    fileExtension: getFileExtension(file.name),
  });

  const handleRemove = (key, file) => {
    setFileLists((prev) => ({
      ...prev,
      [key]: (prev[key] ?? []).filter((f) => f.uid !== file.uid),
    }));
  };

  const handleChange = (key, fileList) => {
    const newList = fileList.map((file) => {
      if (file.originFileObj && file.name) {
        const ext = file.name.includes(".")
          ? `.${file.name.split(".").pop()}`
          : "";
        const base = file.name.replace(ext, "");
        const cleanName = `${removeAccents(base)}${ext}`;

        if (file.originFileObj.name !== cleanName) {
          const cleanFile = new File([file.originFileObj], cleanName, {
            type: file.originFileObj.type,
          });
          return { ...file, name: cleanName, originFileObj: cleanFile };
        }
      }
      return file;
    });

    setFileLists((prev) => ({ ...prev, [key]: newList }));
    if (newList.length > 0) {
      setFileErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  // Fluxo unificado: SEMPRE envia List<DocumentExecutionRequest> ao backend
  // (single = array de 1; pacote = array de N)
  // -> create-upload (array) -> PUT S3 (cada arquivo) -> validate-upload (cada documentId)
  const uploadDocuments = async () => {
    const headers = buildAuthHeaders();

    // Monta o array na MESMA ordem dos uploaders (para casar o índice da resposta)
    const requests = uploaders.map(({ key, documentConfigId }) => {
      const file = fileLists[key][0].originFileObj;
      return buildExecutionRequest({ documentConfigId, file });
    });

    // Backend espera List<DocumentExecutionRequest>
    const responseCreate = await api.post(
      CREATE_UPLOAD_PATH,
      requests,
      headers
    );

    // Esperado: data: [{ url, documentId, documentConfigId }, ...]
    const createdList = Array.isArray(responseCreate.data?.data)
      ? responseCreate.data.data
      : [];

    // PUT S3 + validate por documento criado (em paralelo)
    await Promise.all(
      createdList.map(async (created, index) => {
        const file = fileLists[uploaders[index].key][0].originFileObj;
        try {
          await axios.put(created.url, file, {
            headers: { "Content-Type": file.type },
          });
          await api.put(VALIDATE_UPLOAD_PATH, headers, {
            params: { documentId: created.documentId },
          });
        } catch (err) {
          // rollback individual no S3
          await api.delete(DOCUMENT_PATH(created.documentId), headers);
          throw err;
        }
      })
    );
  };

  const handleSendClick = async () => {
    try {
      // Validação: 1 arquivo obrigatório por uploader
      const errors = {};
      uploaders.forEach(({ key }) => {
        const list = fileLists[key];
        if (!list?.length || !list[0]?.originFileObj) {
          errors[key] = intl.formatMessage({ id: "select_file_required" });
        }
      });

      if (Object.keys(errors).length > 0) {
        setFileErrors(errors);
        return;
      }
      setFileErrors({});
      setIsUploading(true);

      // Mesmo fluxo para single (array de 1) e pacote (array de N)
      await uploadDocuments();

      notification.displaySuccess(
        intl.formatMessage({
          id: isPackage
            ? "package_uploaded_successfully"
            : "document_uploaded_successfully",
        })
      );

      dispatch(
        checkMandatoryDocuments(
          opportunityId,
          () => {},
          () => {}
        )
      );

      setFileLists({});
      setObservation("");
      setIsUploading(false);
      setFileErrors({});
      onFinish({ success: true });
    } catch (err) {
      setIsUploading(false);
      const errorCode = err?.response?.data?.code;
      notification.displayError(
        intl.formatMessage(
          {
            id: errorCode ? `api.error.${errorCode}` : "document_upload_error",
          },
          { allowedFileExtensions }
        ),
        "document-upload-error-message"
      );
    }
  };

  const handleCancel = () => {
    onFinish({ success: false });
    setFileLists({});
    setObservation("");
    setIsUploading(false);
    setFileErrors({});
  };

  const modalTitle = useMemo(() => {
    const action = intl.formatMessage({
      id: isPackage ? "send_package_documents" : "send_document",
    });
    return flowName ? `${flowName} - ${action}` : action;
  }, [flowName, isPackage, intl]);

  return (
    <SendModal
      centered
      closable={false}
      visible={visible}
      footer={[
        <ModalBtnsContainer key="footer">
          <ModalActionBtns
            $isCancel
            onClick={handleCancel}
            disabled={isUploading}
          >
            {intl.formatMessage({ id: "cancel" })}
          </ModalActionBtns>
          <ModalActionBtns
            onClick={handleSendClick}
            disabled={isUploading}
            style={{
              opacity: isUploading ? 0.6 : 1,
              cursor: isUploading ? "not-allowed" : "pointer",
            }}
          >
            {isUploading
              ? intl.formatMessage({ id: "uploading" })
              : intl.formatMessage({ id: "send" })}
          </ModalActionBtns>
        </ModalBtnsContainer>,
      ]}
    >
      <ModalTitle>{modalTitle}</ModalTitle>

      <UploadersScrollArea>
        {uploaders.map(({ key, name, fileTypes: uploaderFileTypes }) => {
          const list = fileLists[key] ?? [];
          const error = fileErrors[key];

          // Para pacotes: usa extensões específicas de cada documento
          // Para single: usa as extensões globais do modal
          const currentAcceptExtensions =
            isPackage && uploaderFileTypes?.length
              ? buildAcceptExtensions(uploaderFileTypes)
              : acceptExtensions;

          const displayExtensions =
            isPackage && uploaderFileTypes?.length
              ? Array.isArray(uploaderFileTypes)
                ? uploaderFileTypes.join(", ")
                : String(uploaderFileTypes)
              : allowedFileExtensions;

          return (
            <div key={key}>
              <p>
                {intl.formatMessage({ id: "upload" })}: {name}
                <span style={{ color: "red" }}>*</span>
              </p>
              <ExtensionInformation>
                {intl.formatMessage(
                  { id: "file_extension_message" },
                  { allowedFileExtensions: displayExtensions }
                )}
              </ExtensionInformation>
              <UploadWrapper isPackage={isPackage}>
                <Dragger
                  required
                  multiple={false}
                  maxCount={1}
                  beforeUpload={() => false}
                  fileList={list}
                  accept={currentAcceptExtensions}
                  onChange={({ fileList }) => handleChange(key, fileList)}
                  itemRender={(_, file) => (
                    <FileItemContainer>
                      <FileItemContent>
                        <FileIcon />
                        <FileNameSpan>{file.name}</FileNameSpan>
                      </FileItemContent>
                      <DeleteIcon onClick={() => handleRemove(key, file)} />
                    </FileItemContainer>
                  )}
                >
                  {!isPackage && <UploadIcon />}
                  <DraggerText>
                    <ClickHere>
                      {intl.formatMessage({ id: "click_here" })}
                    </ClickHere>
                    {intl.formatMessage({ id: "or_drag_and_drop_file" })}
                  </DraggerText>
                </Dragger>
                {error && (
                  <div style={{ color: "red", marginTop: 8, fontSize: 13 }}>
                    {error}
                  </div>
                )}
              </UploadWrapper>
            </div>
          );
        })}
      </UploadersScrollArea>

      <ObsContainer>
        <p>
          {intl.formatMessage({ id: "notes" })}{" "}
          <NotesOptional>
            {intl.formatMessage({ id: "notesOptional" })}
          </NotesOptional>
        </p>
        <ObsInput
          showCount
          maxLength={MAX_OBSERVATION_LENGTH}
          value={observation}
          onChange={(e) => setObservation(e.target.value)}
        />
      </ObsContainer>
    </SendModal>
  );
};

export default SendDocumentModal;
